import { useEffect, useMemo, useState } from 'react';
import { ICountryExchangeDetail } from 'types/countries';
import { IUploadResults } from 'types/bulkUpload';
import { useHistory } from 'react-router-dom';
import { uploadCatalog } from 'services/bulkUpload/uploadCatalog.service';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import { IUploadInfo } from '../UploadCatalog/content/uploadCatalogInfo';
import { templateWithErrorsInfo } from '../UploadCatalog/content/uploadCatalogInfo';
import { templateUploadedInfo } from '../UploadCatalog/content/uploadCatalogInfo';
import BulkUploadHeader from '../components/BulkUploadHeader/BulkUploadHeader';
import { DOWNLOAD_CATALOG_STEPS } from './constants';
import DownloadCatalogStep0 from './components/DownloadCatalogStep0/DownloadCatalogStep0';
import DownloadCatalogStep1 from './components/DownloadCatalogStep1/DownloadCatalogStep1';
import DownloadCatalogStep2 from './components/DownloadCatalogStep2/DownloadCatalogStep2';
import './DownloadUploadCatalog.scss';

const DownloadUploadCatalogModule = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [goBackText, setGoBackText] = useState('');
  const [replaceCatalog, setReplaceCatalog] = useState(true);
  const [disableReplaceCatalog, setDisableReplaceCatalog] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<ICountryExchangeDetail | undefined>();
  const [uploadResults, setUploadResults] = useState<IUploadResults | undefined>();
  const [errors, setErrors] = useState<string[]>([]);
  const [errorTemplateURL, setErrorTemplateURL] = useState<string | undefined>();

  const { push } = useHistory();
  const steps = DOWNLOAD_CATALOG_STEPS;

  const nextStep = () => setCurrentStep(curr => curr + 1);
  const prevStep = () => setCurrentStep(curr => curr - 1);

  const handleSubmitUploadForm = async (file: File) => {
    try {
      if (!selectedCountry?.id) throw new SyntaxError('No se ha seleccionado un país');

      const uploadCatalogResponse = await uploadCatalog(file, replaceCatalog, selectedCountry?.id);
      setErrors(uploadCatalogResponse?.errors || []);
      setUploadResults(uploadCatalogResponse);
      setErrorTemplateURL(uploadCatalogResponse?.errorFileUrl);
      setCurrentStep(curr => curr + 1);
    } catch (error) {
      const errorMessage =
        error instanceof SyntaxError ? error.message : 'Error al subir el catálogo, por favor, inténtelo de nuevo';
      displayErrorNotification({
        customJSXMessage: <>{errorMessage}</>,
        duration: 5000
      });
    }
  };

  const handleBack = () => {
    if (currentStep === 0) {
      push('/bulk-upload/type-of-charge');
    } else {
      prevStep();
    }
  };

  const summaryUploadInfo = useMemo<IUploadInfo>(() => {
    const updatedRecords = uploadResults?.updatedRecords || 0;
    const totalRecords = uploadResults?.totalRecords || 0;
    if (errors && errors.length > 0) {
      return templateWithErrorsInfo(updatedRecords, totalRecords);
    }
    return templateUploadedInfo(updatedRecords, totalRecords);
  }, [errors, uploadResults]);

  const handleReturnToUpload = () => {
    prevStep();
    setReplaceCatalog(false);
    setDisableReplaceCatalog(true);
  };

  const handleReplaceCatalog = (value: boolean) => {
    setReplaceCatalog(value);
  };

  const handleSelectedCountry = (country: ICountryExchangeDetail) => {
    setSelectedCountry(country);
  };

  const renderComponentInStep = (step: number) => {
    switch (step) {
      case 0:
        return (
          <DownloadCatalogStep0
            handleReplaceCatalog={handleReplaceCatalog}
            handleSelectedCountry={handleSelectedCountry}
            nextStep={nextStep}
            replaceCatalog={replaceCatalog}
            selectedCountry={selectedCountry}
            disableReplaceCatalog={disableReplaceCatalog}
          />
        );
      case 1:
        return (
          <DownloadCatalogStep1
            disableReplaceCatalog={disableReplaceCatalog}
            handleSubmitUploadForm={handleSubmitUploadForm}
            handleReplaceCatalog={handleReplaceCatalog}
            handleSelectedCountry={handleSelectedCountry}
            hasErrors={errors && errors.length > 0}
            replaceCatalog={replaceCatalog}
            selectedCountry={selectedCountry}
            errorTemplateURL={errorTemplateURL}
          />
        );
      case 2:
        return (
          <DownloadCatalogStep2
            errorTemplateURL={errorTemplateURL}
            handleReturnToUpload={handleReturnToUpload}
            replaceCatalog={replaceCatalog}
            selectedCountry={selectedCountry}
            summaryUploadInfo={summaryUploadInfo}
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    switch (currentStep) {
      case 0:
        setGoBackText('Volver a Cargue');
        break;
      case 1:
        setGoBackText(steps[0]);
        break;
      case 2:
        setGoBackText(steps[1]);
        break;
      default:
        setGoBackText('');
    }
  }, [currentStep, steps])

  return (
    <main className="downloadUploadCatalogContainer">
      <BulkUploadHeader steps={steps} currentStep={currentStep} handleBack={handleBack} goBackText={goBackText} />
      {renderComponentInStep(currentStep)}
    </main>
  );
};

export default DownloadUploadCatalogModule;
